import { useMemo, useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, formatCurrencyOption, getCountryCurrency, getHomeCurrency } from "@/utils/currencies";

const OTHER_SENTINEL = "__other__";

/**
 * Currency dropdown that shows a short relevant list first (home currency +
 * segment's country currency + POINTS) and reveals the full currency list
 * when the user chooses "Other currency…".
 *
 * Props:
 *   value              - current currency code
 *   onChange(value)    - setter
 *   segmentCountry     - country name for the linked segment (optional)
 *   includePoints      - add POINTS option (default: true)
 *   id, testId         - passthrough
 */
const CurrencySelectFiltered = ({
  value,
  onChange,
  segmentCountry,
  includePoints = true,
  id,
  testId = "payment-currency-select",
  className = "text-sm",
}) => {
  const home = getHomeCurrency();
  const countryCode = getCountryCurrency(segmentCountry);

  // Relevant codes deduped, in sensible order: home first, then country, then POINTS
  const relevantCodes = useMemo(() => {
    const list = [];
    if (home) list.push(home);
    if (countryCode && countryCode !== home) list.push(countryCode);
    return list;
  }, [home, countryCode]);

  const valueInRelevant =
    !value || relevantCodes.includes(value) || (includePoints && value === "POINTS");
  const [showAll, setShowAll] = useState(!valueInRelevant);

  useEffect(() => {
    if (!valueInRelevant && !showAll) setShowAll(true);
  }, [valueInRelevant, showAll]);

  const otherCurrencies = useMemo(() => {
    const relevantSet = new Set(relevantCodes);
    return CURRENCIES.filter(c => !relevantSet.has(c.code));
  }, [relevantCodes]);

  const handleChange = (v) => {
    if (v === OTHER_SENTINEL) {
      setShowAll(true);
      return;
    }
    onChange(v);
  };

  const labelFor = (code) => {
    const c = CURRENCIES.find(x => x.code === code);
    return c ? formatCurrencyOption(c) : code;
  };

  return (
    <Select value={value} onValueChange={handleChange}>
      <SelectTrigger id={id} className={className} data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" className="max-h-[300px]">
        {relevantCodes.length > 0 && (
          <>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Suggested
            </div>
            {relevantCodes.map((code) => (
              <SelectItem key={`s-${code}`} value={code}>
                {labelFor(code)}
                {code === home && <span className="ml-1 text-[10px] text-gray-400">(home)</span>}
                {code === countryCode && code !== home && <span className="ml-1 text-[10px] text-gray-400">(local)</span>}
              </SelectItem>
            ))}
            {includePoints && (
              <SelectItem value="POINTS">POINTS</SelectItem>
            )}
            {!showAll && (
              <SelectItem value={OTHER_SENTINEL} data-testid="currency-select-other">
                — Other currency… —
              </SelectItem>
            )}
          </>
        )}
        {(showAll || relevantCodes.length === 0) && (
          <>
            {relevantCodes.length > 0 && (
              <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 border-t dark:border-gray-700">
                All currencies
              </div>
            )}
            {otherCurrencies.map((currency) => (
              <SelectItem key={currency.code} value={currency.code}>
                {formatCurrencyOption(currency)}
              </SelectItem>
            ))}
            {includePoints && relevantCodes.length === 0 && (
              <SelectItem value="POINTS">POINTS</SelectItem>
            )}
          </>
        )}
      </SelectContent>
    </Select>
  );
};

export default CurrencySelectFiltered;
